import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { ArrowLeft, Eye, Users, Wifi, WifiOff, RefreshCw, Trophy, Gamepad2, Search, MessageSquare, Send, DollarSign, TrendingUp, Wallet, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import drawBackground from '../game/background';
import { GROUND_HEIGHT, getDrawY, DEFAULT_GROUND_Y, getDepthScale } from '../game/constants';
import { createPurchaseTransaction, sendAndConfirmTransaction } from '../utils/solanaTransactions';

import { API_BASE as API, WS_URL } from '../lib/backendUrl';

// === SPECTATOR CANVAS: Renders received game state ===
const SpectatorCanvas = ({ lobbyId, onBack, tournamentId = null, matchId = null }) => {
  const { publicKey, signTransaction, connected: walletConnected } = useWallet();
  const { connection } = useConnection();
  const { setVisible: showWalletModal } = useWalletModal();
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [players, setPlayers] = useState({});
  const [lobbyInfo, setLobbyInfo] = useState(null);
  const animRef = useRef(null);
  const gameStateRef = useRef({ players: {}, enemies: [], bullets: [] });

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(true);
  const [viewerName] = useState(() => 'Viewer' + Math.floor(Math.random() * 9999));
  const chatEndRef = useRef(null);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const reactionIdRef = useRef(0);

  const REACTIONS = ['GG', 'WOW', 'RIP', 'NICE', 'POG', 'LUL', 'EZ'];
  const REACTION_COLORS = {
    GG: '#14F195', WOW: '#FFD700', RIP: '#FF3B30', NICE: '#5AC8FA',
    POG: '#FF9500', LUL: '#9945FF', EZ: '#14F195'
  };

  // Betting state
  const [showBetting, setShowBetting] = useState(!!tournamentId);
  const [betAmount, setBetAmount] = useState(100);
  const [matchBets, setMatchBets] = useState({ total_pool: 0, player_pools: {}, odds: {}, bet_count: 0 });
  const [myBet, setMyBet] = useState(null);
  const [betLoading, setBetLoading] = useState(false);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Fade out floating reactions
  useEffect(() => {
    if (floatingReactions.length === 0) return;
    const timer = setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => Date.now() - r.time < 3000));
    }, 500);
    return () => clearTimeout(timer);
  }, [floatingReactions]);

  // WebSocket connection
  useEffect(() => {
    if (!lobbyId) return;
    let ws;
    let pingInterval;

    const connect = () => {
      ws = new WebSocket(`${WS_URL}/api/ws/spectate/${lobbyId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 15000);
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingInterval);
        setTimeout(() => { if (wsRef.current === ws) connect(); }, 3000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'lobby_state':
              setLobbyInfo(data.lobby);
              if (data.spectator_count) setSpectatorCount(data.spectator_count);
              break;
            case 'player_state':
              gameStateRef.current.players[data.wallet_address] = data.state;
              setPlayers(prev => ({ ...prev, [data.wallet_address]: data.state }));
              break;
            case 'enemy_sync':
              gameStateRef.current.enemies = data.enemies || [];
              break;
            case 'bullet_sync':
              gameStateRef.current.bullets = data.bullets || [];
              break;
            case 'spectator_update':
              setSpectatorCount(data.count);
              break;
            case 'spectator_chat':
              setChatMessages(prev => [...prev.slice(-80), {
                name: data.name,
                message: data.message,
                time: Date.now()
              }]);
              break;
            case 'spectator_reaction':
              setFloatingReactions(prev => [...prev.slice(-15), {
                id: reactionIdRef.current++,
                name: data.name,
                reaction: data.reaction,
                time: Date.now(),
                x: 50 + Math.random() * 40,
                y: 20 + Math.random() * 60
              }]);
              break;
            case 'pong':
              break;
            default:
              break;
          }
        } catch (e) {}
      };
    };

    connect();
    return () => {
      clearInterval(pingInterval);
      wsRef.current = null;
      if (ws) ws.close();
    };
  }, [lobbyId]);

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'spectator_chat',
      name: viewerName,
      message: chatInput.trim()
    }));
    setChatInput('');
  };

  const sendReaction = (reaction) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'spectator_reaction',
      name: viewerName,
      reaction
    }));
  };

  // Fetch match bets
  const fetchBets = useCallback(async () => {
    if (!tournamentId || !matchId) return;
    try {
      const res = await axios.get(`${API}/bet/match/${tournamentId}/${matchId}`);
      setMatchBets(res.data);
      const existing = res.data.bets?.find(b => b.bettor_name === viewerName);
      if (existing) setMyBet(existing);
    } catch (e) {}
  }, [tournamentId, matchId, viewerName]);

  useEffect(() => {
    if (tournamentId && matchId) {
      fetchBets();
      const interval = setInterval(fetchBets, 8000);
      return () => clearInterval(interval);
    }
  }, [fetchBets, tournamentId, matchId]);

  const placeBet = async (playerId) => {
    if (!tournamentId || !matchId || myBet || betLoading) return;
    
    // Require wallet for real SML transfer
    if (!publicKey || !signTransaction) {
      showWalletModal(true);
      toast.error('Connect your wallet to place a bet');
      return;
    }
    
    setBetLoading(true);
    try {
      // 1. Get treasury wallet
      const treasuryRes = await axios.get(`${API}/bet/treasury`);
      const treasuryWallet = treasuryRes.data.treasury_wallet;
      
      // 2. Create on-chain SML transfer to treasury
      toast.info(`Sending ${betAmount} SML to betting pool...`);
      const tx = await createPurchaseTransaction(
        connection, publicKey, treasuryWallet, betAmount
      );
      
      // 3. Sign and send transaction
      const signedTx = await signTransaction(tx);
      const sig = await sendAndConfirmTransaction(connection, signedTx);
      toast.success('Transaction confirmed!');
      
      // 4. Record bet with tx signature
      const res = await axios.post(`${API}/bet/place/${tournamentId}/${matchId}`, {
        bettor_id: publicKey.toBase58(),
        bettor_name: `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}`,
        player_id: playerId,
        amount: betAmount,
        tx_signature: sig
      });
      setMyBet(res.data.bet);
      toast.success(`Bet placed: ${betAmount} SML on-chain`);
      fetchBets();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to place bet';
      toast.error(msg);
    } finally { setBetLoading(false); }
  };

  // Claim winnings
  const claimWinnings = async () => {
    if (!myBet || myBet.status !== 'won' || myBet.claimed) return;
    toast.info(`Winnings of ${myBet.payout} SML will be processed.`);
    try {
      await axios.post(`${API}/bet/claim/${myBet.bet_id}`);
      toast.success(`Claimed ${myBet.payout} SML!`);
      setMyBet(prev => ({ ...prev, claimed: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim');
    }
  };

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const render = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const state = gameStateRef.current;
      const playerList = Object.values(state.players);

      // Determine camera from first player
      let cameraX = 0;
      if (playerList.length > 0) {
        cameraX = Math.max(0, (playerList[0].x || 0) - w / 3);
      }

      // Draw background
      const level = playerList[0]?.level || 1;
      drawBackground(ctx, cameraX, w, h, level);

      // Draw enemies
      (state.enemies || []).forEach(enemy => {
        const ex = (enemy.x || 0) - cameraX;
        const ey = enemy.y || h - GROUND_HEIGHT - 50;
        if (ex < -100 || ex > w + 100) return;
        const scale = getDepthScale(enemy.groundY || DEFAULT_GROUND_Y);
        ctx.save();
        ctx.translate(ex + 20, ey + 25);
        ctx.scale(scale, scale);
        ctx.translate(-(ex + 20), -(ey + 25));
        ctx.fillStyle = enemy.color || '#FF3B30';
        ctx.fillRect(ex + 5, ey + 5, 30, 40);
        ctx.fillStyle = '#333';
        ctx.fillRect(ex + 10, ey, 20, 15);
        ctx.fillStyle = '#FF0000';
        ctx.fillRect(ex + 13, ey + 5, 4, 4);
        ctx.fillRect(ex + 23, ey + 5, 4, 4);
        // Health bar
        if (enemy.health !== undefined && enemy.maxHealth) {
          ctx.fillStyle = '#333';
          ctx.fillRect(ex, ey - 8, 40, 4);
          ctx.fillStyle = '#FF3B30';
          ctx.fillRect(ex, ey - 8, (enemy.health / enemy.maxHealth) * 40, 4);
        }
        ctx.restore();
      });

      // Draw bullets
      (state.bullets || []).forEach(bullet => {
        const bx = (bullet.x || 0) - cameraX;
        const by = bullet.y || 0;
        if (bx < -10 || bx > w + 10) return;
        ctx.fillStyle = bullet.isEnemy ? '#FF3B30' : '#14F195';
        ctx.fillRect(bx, by, 8, 3);
      });

      // Draw players
      playerList.forEach((p, i) => {
        const px = (p.x || 0) - cameraX;
        const py = p.y || h - GROUND_HEIGHT - 60;
        if (px < -100 || px > w + 100) return;
        const scale = getDepthScale(p.groundY || DEFAULT_GROUND_Y);
        ctx.save();
        ctx.translate(px + 25, py + 37);
        ctx.scale(scale, scale);
        ctx.translate(-(px + 25), -(py + 37));
        // Body
        const color = p.color || ['#14F195', '#9945FF', '#5AC8FA', '#FFD700'][i % 4];
        ctx.fillStyle = color;
        ctx.fillRect(px + 5, py + 25, 40, 30);
        // Head
        ctx.fillStyle = '#FFCC99';
        ctx.fillRect(px + 10, py, 30, 25);
        // Eyes
        ctx.fillStyle = '#000';
        ctx.fillRect(px + 15, py + 8, 5, 5);
        ctx.fillRect(px + 28, py + 8, 5, 5);
        // Legs
        ctx.fillStyle = color;
        ctx.fillRect(px + 10, py + 52, 12, 18);
        ctx.fillRect(px + 28, py + 52, 12, 18);
        // Direction indicator
        if (p.direction === -1) {
          ctx.fillStyle = '#333';
          ctx.fillRect(px - 5, py + 35, 12, 5);
        } else {
          ctx.fillStyle = '#333';
          ctx.fillRect(px + 42, py + 35, 12, 5);
        }
        // Health bar
        ctx.fillStyle = '#333';
        ctx.fillRect(px, py - 12, 50, 6);
        ctx.fillStyle = (p.health || 100) > 30 ? '#14F195' : '#FF3B30';
        ctx.fillRect(px, py - 12, ((p.health || 100) / 100) * 50, 6);
        // Name tag
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px "Press Start 2P", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(p.nftId?.replace('nft_', '') || `P${i + 1}`, px + 25, py - 18);
        ctx.textAlign = 'left';
        ctx.restore();
      });

      // Spectator HUD overlay
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(0, 0, w, 36);
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 11px "Press Start 2P", monospace';
      ctx.fillText('SPECTATING', 12, 24);

      // Player scores
      let scoreX = 180;
      playerList.forEach((p, i) => {
        const color = p.color || ['#14F195', '#9945FF', '#5AC8FA', '#FFD700'][i % 4];
        ctx.fillStyle = color;
        ctx.fillRect(scoreX, 8, 10, 10);
        ctx.fillStyle = '#fff';
        ctx.font = '9px "Press Start 2P", monospace';
        ctx.fillText(`${p.score || 0}`, scoreX + 16, 17);
        ctx.fillStyle = '#888';
        ctx.fillText(`K:${p.kills || 0}`, scoreX + 16, 28);
        scoreX += 120;
      });

      // Spectator count
      ctx.fillStyle = '#FF9500';
      ctx.font = '9px "Press Start 2P", monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${spectatorCount} watching`, w - 12, 24);
      ctx.textAlign = 'left';

      animRef.current = requestAnimationFrame(render);
    };

    animRef.current = requestAnimationFrame(render);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [spectatorCount]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 z-10">
        <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="spectator-back-btn">
          <ArrowLeft className="w-5 h-5 mr-2" /> Leave
        </Button>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 text-xs ${connected ? 'text-[#14F195]' : 'text-[#FF3B30]'}`}>
            {connected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {connected ? 'Live' : 'Reconnecting...'}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#FF9500]">
            <Eye className="w-4 h-4" /> {spectatorCount}
          </div>
          <Button onClick={() => setShowChat(p => !p)} variant="ghost"
            className={`text-xs p-1 ${showChat ? 'text-[#14F195]' : 'text-[#666]'}`} data-testid="toggle-chat">
            <MessageSquare className="w-4 h-4" />
          </Button>
          {tournamentId && (
            <Button onClick={() => setShowBetting(p => !p)} variant="ghost"
              className={`text-xs p-1 ${showBetting ? 'text-[#FFD700]' : 'text-[#666]'}`} data-testid="toggle-betting">
              <DollarSign className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main area: Canvas + Chat */}
      <div className="flex-1 flex gap-2 p-2 min-h-0">
        {/* Game Canvas */}
        <div className="flex-1 flex items-center justify-center relative">
          <canvas
            ref={canvasRef}
            width={960}
            height={540}
            className="w-full border-2 border-[#333] rounded-lg"
            style={{ imageRendering: 'pixelated', maxHeight: '75vh' }}
            data-testid="spectator-canvas"
          />
          {/* Floating reactions */}
          {floatingReactions.map(r => {
            const age = (Date.now() - r.time) / 3000;
            return (
              <div key={r.id} className="absolute pointer-events-none font-game text-lg transition-all"
                style={{
                  left: `${r.x}%`,
                  top: `${r.y - age * 20}%`,
                  opacity: 1 - age,
                  color: REACTION_COLORS[r.reaction] || '#fff',
                  textShadow: `0 0 8px ${REACTION_COLORS[r.reaction] || '#fff'}`,
                  fontSize: '18px'
                }}>
                {r.reaction}
              </div>
            );
          })}
        </div>

        {/* Betting Panel */}
        {showBetting && tournamentId && (
          <div className="w-64 flex flex-col bg-[#0a0a0a] border border-[#333] rounded-lg overflow-hidden flex-shrink-0" data-testid="betting-panel">
            <div className="px-3 py-2 border-b border-[#333] flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#FFD700]" />
              <span className="text-xs font-game text-[#FFD700]">BETS</span>
              <span className="text-[10px] text-[#666] ml-auto">{matchBets.bet_count} bets</span>
            </div>

            {/* Pool info */}
            <div className="p-3 border-b border-[#222]">
              <div className="text-center mb-2">
                <p className="text-[10px] text-[#666]">TOTAL POOL</p>
                <p className="font-game text-lg text-[#FFD700]">{matchBets.total_pool.toLocaleString()} SML</p>
              </div>

              {/* Player odds */}
              {Object.entries(matchBets.player_pools || {}).map(([pid, pool]) => {
                const odds = matchBets.odds?.[pid] || 0;
                const pct = matchBets.total_pool > 0 ? Math.round(pool / matchBets.total_pool * 100) : 50;
                const playerState = Object.values(players).find(p => p?.nftId === pid || true);
                return (
                  <div key={pid} className="flex items-center justify-between py-1">
                    <span className="text-xs text-[#ccc] truncate max-w-[80px]">{pid.slice(0, 10)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#FF9500]">{pct}%</span>
                      <span className="text-[10px] text-[#14F195]">{odds}x</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Place bet */}
            {!myBet ? (
              <div className="p-3 space-y-2">
                {!walletConnected ? (
                  <div className="text-center space-y-2">
                    <p className="text-[10px] text-[#A1A1AA]">Connect wallet to bet with SML tokens</p>
                    <Button onClick={() => showWalletModal(true)}
                      className="w-full bg-gradient-to-r from-[#9945FF] to-[#14F195] text-white text-xs py-2"
                      data-testid="bet-connect-wallet">
                      <Wallet className="w-3 h-3 mr-1" /> Connect Wallet
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-[10px] text-[#A1A1AA] text-center">WAGER SML TOKENS</p>
                    <div className="flex gap-1 justify-center">
                      {[50, 100, 250, 500].map(amt => (
                        <button key={amt} onClick={() => setBetAmount(amt)}
                          className={`px-2 py-1 text-[9px] font-game rounded ${betAmount === amt ? 'bg-[#FFD700] text-black' : 'bg-[#222] text-[#888]'}`}
                          data-testid={`bet-amount-${amt}`}>
                          {amt}
                        </button>
                      ))}
                    </div>
                    {lobbyInfo?.players?.map(p => (
                      <Button key={p.wallet_address} onClick={() => placeBet(p.wallet_address)}
                        disabled={betLoading || !!myBet}
                        className="w-full bg-[#1a1a1a] border border-[#333] hover:border-[#FFD700] text-xs py-2"
                        data-testid={`bet-on-${p.wallet_address}`}>
                        {betLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TrendingUp className="w-3 h-3 mr-1 text-[#FFD700]" />}
                        {betAmount} SML on {p.name || p.wallet_address?.slice(0, 8)}
                      </Button>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div className="p-3 text-center space-y-2">
                <p className="text-[10px] text-[#14F195] mb-1">BET PLACED</p>
                <p className="font-game text-sm text-[#FFD700]">{myBet.amount} SML</p>
                <p className="text-[10px] text-[#A1A1AA]">
                  on {myBet.player_id?.slice(0, 8)}...
                </p>
                {myBet.tx_signature && (
                  <p className="text-[8px] text-[#666] truncate" title={myBet.tx_signature}>
                    TX: {myBet.tx_signature.slice(0, 16)}...
                  </p>
                )}
                <p className={`text-xs font-game ${
                  myBet.status === 'won' ? 'text-[#14F195]' : myBet.status === 'lost' ? 'text-[#FF3B30]' : 'text-[#FF9500]'
                }`}>
                  {myBet.status === 'won' ? `WON ${myBet.payout} SML` : myBet.status === 'lost' ? 'LOST' : 'PENDING'}
                </p>
                {myBet.status === 'won' && !myBet.claimed && (
                  <Button onClick={claimWinnings}
                    className="w-full bg-[#14F195] text-black text-xs py-2 font-game"
                    data-testid="claim-winnings-btn">
                    <Check className="w-3 h-3 mr-1" /> CLAIM {myBet.payout} SML
                  </Button>
                )}
                {myBet.claimed && (
                  <p className="text-[10px] text-[#14F195] flex items-center justify-center gap-1">
                    <Check className="w-3 h-3" /> Claimed
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chat Panel */}
        {showChat && (
          <div className="w-72 flex flex-col bg-[#0a0a0a] border border-[#333] rounded-lg overflow-hidden flex-shrink-0" data-testid="spectator-chat">
            {/* Chat header */}
            <div className="px-3 py-2 border-b border-[#333] flex items-center justify-between">
              <span className="text-xs font-game text-[#FF9500] flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> CHAT
              </span>
              <span className="text-[10px] text-[#666]">{viewerName}</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0" style={{ maxHeight: '50vh' }}>
              {chatMessages.length === 0 && (
                <p className="text-[#333] text-xs text-center py-8">No messages yet...</p>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className="text-xs">
                  <span className="text-[#FF9500] font-bold">{msg.name}: </span>
                  <span className="text-[#ccc]">{msg.message}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Reaction bar */}
            <div className="px-2 py-1.5 border-t border-[#222] flex gap-1 flex-wrap justify-center">
              {REACTIONS.map(r => (
                <button key={r} onClick={() => sendReaction(r)}
                  className="px-2 py-0.5 rounded text-[9px] font-game transition-all hover:scale-110 active:scale-90"
                  style={{ backgroundColor: `${REACTION_COLORS[r]}20`, color: REACTION_COLORS[r], border: `1px solid ${REACTION_COLORS[r]}40` }}
                  data-testid={`reaction-${r}`}>
                  {r}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="p-2 border-t border-[#333] flex gap-1">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendChat()}
                placeholder="Say something..."
                className="flex-1 bg-[#1a1a1a] border border-[#333] rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#FF9500]"
                maxLength={200}
                data-testid="spectator-chat-input"
              />
              <Button onClick={sendChat} className="bg-[#FF9500] text-black px-2 py-1" data-testid="spectator-chat-send">
                <Send className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Info bar */}
      <div className="p-2 flex items-center justify-center gap-6 text-xs text-[#A1A1AA]">
        {lobbyInfo && (
          <>
            <span className="flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> {lobbyInfo.lobby_name || 'Live Match'}</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {lobbyInfo.players?.length || 0} players</span>
          </>
        )}
      </div>
    </div>
  );
};

// === SPECTATOR BROWSER: Lists available matches ===
const SpectatorBrowser = ({ onBack, onWatch }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/spectate/matches`);
      setMatches(res.data.matches || []);
    } catch (e) {
      console.error('Error fetching spectatable matches:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 8000);
    return () => clearInterval(interval);
  }, [fetchMatches]);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          <Button onClick={fetchMatches} className="bg-[#333] hover:bg-[#444]" data-testid="refresh-spectate">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="font-game text-3xl md:text-4xl text-[#FF9500] mb-2 flex items-center justify-center gap-3">
            <Eye className="w-8 h-8" /> SPECTATE
          </h1>
          <p className="text-[#A1A1AA] text-sm">Watch live matches in real-time</p>
        </div>

        <div className="glass-card p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#FF9500] border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Searching for live matches...</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <Eye className="w-12 h-12 text-[#333] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No live matches right now</p>
              <p className="text-[#666] text-sm mt-2">Check back when players are in-game!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match, i) => (
                <div key={`${match.id}-${i}`}
                  className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg border border-[#333] hover:border-[#FF9500] transition-colors"
                  data-testid={`spectate-match-${match.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#FF9500]/20 rounded-lg flex items-center justify-center">
                      {match.type === 'tournament' 
                        ? <Trophy className="w-6 h-6 text-[#FFD700]" />
                        : <Gamepad2 className="w-6 h-6 text-[#FF9500]" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-game text-white text-sm">{match.name}</span>
                        <span className="text-[8px] px-2 py-0.5 rounded bg-[#FF9500]/20 text-[#FF9500] uppercase">
                          {match.type === 'tournament' ? `R${match.round}` : 'LIVE'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#A1A1AA] mt-1">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {match.players?.join(' vs ')}
                        </span>
                        {match.spectators > 0 && (
                          <span className="flex items-center gap-1 text-[#FF9500]">
                            <Eye className="w-3 h-3" /> {match.spectators}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => onWatch(match.id)}
                    className="bg-[#FF9500] text-black hover:bg-[#FF9500]/90 font-game text-sm"
                    data-testid={`watch-btn-${match.id}`}>
                    <Eye className="w-4 h-4 mr-2" /> WATCH
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SpectatorBrowser, SpectatorCanvas };
export default SpectatorBrowser;
